import { Component, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';

import { UiSwitchService } from '../../../services/ui-switch.service';
import { loginSuccess } from '../../../state/auth/auth.actions';
import { resetState } from '../../../state/global/global.actions';
import * as SubscriptionActions from '../../../state/subscription/subscription.actions';

/**
 * /auth/handoff?code=… — landing page when a user switches back from the new UI.
 * Exchanges the single-use code for a legacy session, then opens the requested page.
 */
@Component({
  selector: 'app-ui-handoff',
  standalone: true,
  imports: [NgIf],
  template: `
    <div class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <div *ngIf="!error" class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
        <p class="text-sm text-gray-600">{{ error || 'Switching to the previous UI…' }}</p>
      </div>
    </div>
  `,
})
export class UiHandoffComponent implements OnInit {
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private uiSwitch: UiSwitchService,
  ) {}

  async ngOnInit(): Promise<void> {
    const code = this.route.snapshot.queryParamMap.get('code');
    if (!code) {
      this.fail();
      return;
    }

    try {
      const { token, user, path } = await this.uiSwitch.exchange(code);

      // Drop any previous session (possibly another user's) before adopting the new one.
      this.store.dispatch(resetState());
      this.store.dispatch(loginSuccess({ token, user }));
      this.store.dispatch(
        SubscriptionActions.storeSubscriptionData({
          ai_tokens: user.ai_tokens,
          credits: user.credits,
          on_trial: user.on_trial,
          subscribed: user.subscribed,
        }),
      );

      const target = !user.has_completed_setup ? '/onboarding' : path && path !== '/' ? path : '/dashboard';
      await this.router.navigateByUrl(target, { replaceUrl: true });
    } catch {
      this.fail();
    }
  }

  private fail(): void {
    this.error = 'This switch link has expired. Redirecting to sign in…';
    setTimeout(() => this.router.navigate(['/login'], { replaceUrl: true, queryParams: { handoff: 'expired' } }), 1500);
  }
}
